import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import MarketplaceShell from './MarketplaceShell';
import MarketplaceErrorScreen from './MarketplaceErrorScreen';
import StepIndicator, { WIZARD_STEP_LABELS } from './StepIndicator';
import { UseCaseCard, UseCaseCardSkeleton, USE_CASE_META } from './useCaseMeta';
import { MktCard, Pill, Tag, Checkbox, PriceTag, WizardNav, PillSkeletons } from './marketplaceUi';
import { saveDraft, loadDraft, resolveWizardUseCase } from './wizardDraft';
import SegmentHoverCard, { useSegmentHover } from './SegmentHoverCard';
import { MARKETPLACE_FILL_PATH } from './marketplaceLinks';
import {
  getUseCases,
  getSubCategories,
  getArchetypes,
  getMarketplaceState,
  getSelectionTwinCount,
  dollars,
  type UseCase,
  type SubCategory,
  type Archetype,
  type StudySelection,
  type TwinCountResult,
} from './marketplaceApi';

// This screen is the PAID builder. It must only ever resume from a PAID draft — a
// free-flow draft (paid !== true) that reaches here with no router state (e.g.
// PostStudyScreen navigating to /run/build) must NOT seed the paid audience or, worse,
// reuse its free studyId in the paid flow. Load through this gate everywhere.
const loadPaidDraft = () => {
  const d = loadDraft();
  return d?.paid ? d : null;
};

// Debounce window before asking the server to re-count the current selection.
// Matches the 300ms filter-debounce used in UnifiedSegmentsSelector.
const COUNT_DEBOUNCE_MS = 300;

// Soft guidance only (never blocks): below this many Twins, results carry a
// low-sample reliability caveat.
const MIN_RECOMMENDED_TWINS = 30;

/**
 * Paid study — ported from pay_per_study.html. Two internal screens:
 * (1) choose a use case, (2) build an audience by selecting sub-categories and
 * archetypes (checkbox model — each selected archetype contributes all its
 * Twins).
 *
 * Cost is billed per DISTINCT profile. Because a persona can belong to several
 * breakfast categories at once, the grand-total Twin count and price come from
 * the server (which de-dupes across the selection) — NEVER from summing
 * per-archetype bucket sizes, which would over-count and over-charge.
 */
const BuildAudienceScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const preset = (location.state || {}) as {
    useCase?: string;
    // Carried back from Set-up/Review (see FillStudyScreen.backToAudienceStep) so a
    // paid user who presses Back does NOT lose their built audience or spawn a
    // duplicate study. The free flow restores the same way in UseCasePicker.
    selections?: StudySelection[];
    twinCount?: number;
    studyId?: number;
  };
  // Rebuild the builder's slug -> archetype-id Set model from the carried
  // selections, computed once at mount (empty for a fresh build → identical to the
  // prior behaviour).
  // Browser Back / reload drops router state; fall back to the session draft so the built
  // audience comes back instead of resetting to blank. `preset` (in-app nav) wins.
  // Read ONCE at mount (useState initializer) — loadDraft can clearDraft() on expiry (a
  // write), so it must not run during every render.
  const [restoredDraft] = useState(loadPaidDraft);
  const restoredSelections = preset.selections ?? restoredDraft?.selections ?? [];
  const restoredRef = useRef<{ sel: Record<string, Set<string>>; cats: string[] } | null>(null);
  if (restoredRef.current === null) {
    const sel: Record<string, Set<string>> = {};
    const cats: string[] = [];
    for (const s of restoredSelections) {
      if (!s.archetype_id) continue;
      (sel[s.sub_category] ??= new Set()).add(s.archetype_id);
      if (!cats.includes(s.sub_category)) cats.push(s.sub_category);
    }
    restoredRef.current = { sel, cats };
  }
  const restored = restoredRef.current;
  // Deep-linked with a use case (e.g. a returning user from a dashboard card) →
  // skip straight to the audience builder.
  // The internal step lives in the URL (?step=audience) — the same pattern as
  // UseCasePicker — so setSearchParams PUSHES a history entry and the browser
  // Back button moves between the two screens instead of leaving the wizard
  // entirely and silently resetting it (#5/#73). Component state here meant Back
  // popped the single /build entry, remounting the screen at step 1.
  const [searchParams, setSearchParams] = useSearchParams();
  const screen: 1 | 2 = searchParams.get('step') === 'audience' ? 2 : 1;
  // The use case rides in the URL with the step, so a browser Back to the audience screen
  // restores it rather than defaulting to the first card (which rendered the survey form
  // with no image upload for what should have been packaging).
  const setScreen = (n: 1 | 2) =>
    setSearchParams(
      n === 2 ? (useCase ? { step: 'audience', useCase } : { step: 'audience' }) : {},
    );

  // Deep-linked with a use case → jump straight to the audience step, carrying the use
  // case in the URL so it survives a later browser Back.
  useEffect(() => {
    if (preset.useCase && searchParams.get('step') !== 'audience') {
      setSearchParams({ step: 'audience', useCase: preset.useCase }, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [useCases, setUseCases] = useState<UseCase[]>([]);
  const [subs, setSubs] = useState<SubCategory[]>([]);
  const [unitPrice, setUnitPrice] = useState(500);
  // URL first (survives browser Back/Forward), then router state, then the PAID-gated
  // session draft (restoredDraft = loadPaidDraft — never a bare loadDraft, which would
  // resume a free-flow draft in the paid builder).
  const [useCase, setUseCase] = useState<string | null>(() =>
    resolveWizardUseCase(
      searchParams.get('useCase'),
      preset.useCase,
      (uc) => uc in USE_CASE_META,
      restoredDraft?.useCase,
    ),
  );
  // Keep ?useCase= in step with the selection so a reload / browser Forward re-enters with
  // the CURRENT use case, not the one the entry was first pushed with (state→URL was
  // otherwise one-way). Replace, not push, so card-hopping doesn't spam history.
  const selectUseCase = (key: string) => {
    setUseCase(key);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('useCase', key);
        return next;
      },
      { replace: true },
    );
  };
  // Parity with UseCasePicker: if the URL says the audience step but there is no use case
  // (a stale link, or an expired draft), fall back to the use-case screen instead of
  // rendering a blank dead-end. Must fire while useCase is still null — before the
  // "preselect the first card" fallback below can turn it into survey. With the URL/draft
  // resolution above this is now rare.
  useEffect(() => {
    if (screen === 2 && !useCase) setSearchParams({}, { replace: true });
  }, [screen, useCase, setSearchParams]);

  // Forward-half of the sync (see UseCasePicker): replace fixes reload but not browser
  // Forward (replaceState can't truncate the forward stack), so on (re-)entry to screen 2
  // rewrite the URL to the live selection — which persists across the in-SPA Forward — so a
  // later reload reads the current use case, not the stale one the entry was pushed with.
  useEffect(() => {
    if (screen === 2 && useCase && searchParams.get('useCase') !== useCase) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('useCase', useCase);
          return next;
        },
        { replace: true },
      );
    }
  }, [screen, useCase, searchParams, setSearchParams]);
  const [activeCats, setActiveCats] = useState<string[]>(restored.cats);
  const [cache, setCache] = useState<Record<string, Archetype[]>>({});
  // sub-category slug -> set of selected archetype_ids
  const [sel, setSel] = useState<Record<string, Set<string>>>(restored.sel);
  const [error, setError] = useState<string | null>(null);
  // The segment whose hover-preview card is showing (null = hidden). Built from the catalog's
  // own research copy; tags + quote are left undefined until that copy lands.
  // Segment hover-preview card (name + twin count + description; tags/quote wired in later).
  const { hoverSeg, show: showSegment, hide: hideSegment } = useSegmentHover();
  // Server-resolved DISTINCT count (grand total + per-sub-category) tagged with
  // the selection it was computed for, so a still-in-flight recount is never
  // mistaken for the current selection's authoritative count.
  const [counted, setCounted] = useState<{ key: string; result: TwinCountResult } | null>(null);
  const [countLoading, setCountLoading] = useState(false);
  // /sub-categories is slow (measured 3.6s, once 15.6s) — without this the pill
  // row renders as an empty void.
  const [subsLoading, setSubsLoading] = useState(true);
  const countTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestCountKey = useRef<string>('');

  // Independent fetches (see UseCasePicker): the use-case cards must not sit in
  // skeleton state behind the slow /sub-categories call, which screen 2 is the
  // first to read.
  useEffect(() => {
    Promise.all([getUseCases(), getMarketplaceState()])
      .then(([u, st]) => {
        setUseCases(u);
        setUnitPrice(st.twin_unit_price_cents);
        // Preselect the first card (see UseCasePicker) — a deep-linked preset wins.
        setUseCase((current) => current || u[0]?.key || null);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoadingMeta(false));

    getSubCategories()
      .then((s) => {
        setSubs(s);
        // Restoring a carried audience: activate + fetch every restored sub-category
        // so `totals` can rebuild the selection. Fresh build: pre-activate just the
        // first sub-category (unchanged behaviour).
        if (restored.cats.length > 0) restored.cats.forEach((slug) => loadCat(slug, true));
        else if (s[0]) loadCat(s[0].slug, true);
      })
      .catch(() => setSubs([]))
      .finally(() => setSubsLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCat = (slug: string, activate: boolean) => {
    if (activate) setActiveCats((c) => (c.includes(slug) ? c : [...c, slug]));
    if (!cache[slug]) {
      getArchetypes(slug)
        .then((a) => setCache((c) => ({ ...c, [slug]: a })))
        // Non-fatal: a failed category load leaves that category empty rather
        // than replacing the whole builder (and losing the paid selection).
        .catch(() => setCache((c) => ({ ...c, [slug]: [] })));
    }
  };

  const toggleCat = (slug: string) => {
    if (activeCats.includes(slug)) {
      setActiveCats((c) => c.filter((s) => s !== slug));
      setSel((s) => {
        const n = { ...s };
        delete n[slug];
        return n;
      });
    } else {
      loadCat(slug, true);
      setSel((s) => ({ ...s, [slug]: s[slug] || new Set() }));
    }
  };

  // Start with a non-zero selection (mockup never shows "0 selected"): once the
  // pre-activated sub-category's archetypes load, select its first archetype.
  // Skip the "auto-select the first archetype" nicety when we've restored a real
  // selection — otherwise it would tack an extra archetype onto the restored one.
  const preselected = useRef(restored.cats.length > 0);
  useEffect(() => {
    if (preselected.current) return;
    const first = activeCats[0];
    const archs = first ? cache[first] : undefined;
    if (!first || !archs || archs.length === 0) return;
    preselected.current = true;
    setSel((s) =>
      s[first] && s[first].size > 0 ? s : { ...s, [first]: new Set([archs[0].archetype_id]) },
    );
  }, [cache, activeCats]);

  const archsOf = (slug: string) => cache[slug] || [];
  const isAll = (slug: string) => {
    const a = archsOf(slug);
    return a.length > 0 && a.every((x) => sel[slug]?.has(x.archetype_id));
  };
  const toggleAll = (slug: string) => {
    setSel((s) => {
      const next = { ...s };
      next[slug] = isAll(slug) ? new Set() : new Set(archsOf(slug).map((a) => a.archetype_id));
      return next;
    });
  };
  const toggleArch = (slug: string, id: string) => {
    setSel((s) => {
      const set = new Set(s[slug] || []);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { ...s, [slug]: set };
    });
  };

  const totals = useMemo(() => {
    // `provisionalTwins` is a naive per-archetype sum used ONLY as a placeholder
    // until the server returns the authoritative distinct count (correct while a
    // single archetype is selected — no overlap yet). The displayed grand total
    // and price below come from the server's distinct `counted` result, never from
    // this sum: personas can belong to multiple categories AND to multiple cohorts
    // within one category, so summing would double-count and overcharge.
    let provisionalTwins = 0;
    const tags: { label: string; n: number }[] = [];
    const selections: StudySelection[] = [];
    activeCats.forEach((slug) => {
      const archs = archsOf(slug);
      const set = sel[slug];
      if (!set || set.size === 0) return;
      const label = subs.find((s) => s.slug === slug)?.name || slug;
      if (isAll(slug)) {
        // A persona can hold MORE THAN ONE cohort within a single category, so the
        // archetype buckets are NOT disjoint and summing them over-counts anyone in
        // two cohorts. "All of this category" is exactly the category's own distinct
        // twin_count, so use that; fall back to the sum only if the catalog total is
        // missing (worst case an over-count on a placeholder tag, never on price).
        const n =
          subs.find((s) => s.slug === slug)?.twin_count ??
          archs.reduce((acc, a) => acc + a.twin_count, 0);
        tags.push({ label: `${label} · All`, n });
      }
      archs.forEach((a) => {
        if (!set.has(a.archetype_id)) return;
        provisionalTwins += a.twin_count;
        selections.push({
          sub_category: slug,
          archetype_id: a.archetype_id,
          twin_count: a.twin_count,
          name: a.name,
        });
        if (!isAll(slug)) tags.push({ label: a.name, n: a.twin_count });
      });
    });
    return { provisionalTwins, tags, selections };
  }, [activeCats, sel, cache, subs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced distinct-count fetch. A stable primitive key (sorted archetype ids)
  // means we only re-fetch when the selection actually changes, and the cleanup
  // cancels any in-flight timer so rapid clicks collapse into one call.
  const selectionKey = totals.selections
    .map((s) => s.archetype_id)
    .sort()
    .join('|');
  useEffect(() => {
    if (countTimeout.current) clearTimeout(countTimeout.current);
    const selections = totals.selections;
    if (selections.length === 0) {
      setCounted({ key: '', result: { twin_count: 0, by_sub_category: {} } });
      setCountLoading(false);
      return;
    }
    setCountLoading(true);
    latestCountKey.current = selectionKey;
    countTimeout.current = setTimeout(() => {
      getSelectionTwinCount(
        selections.map((s) => ({ sub_category: s.sub_category, archetype_id: s.archetype_id })),
      )
        .then((r) => setCounted({ key: selectionKey, result: r }))
        // A failed recount is non-fatal: keep the provisional count (never 0
        // while something is selected) rather than wiping the whole builder.
        .catch(() => {})
        // Only clear the spinner if this is still the newest selection — a stale
        // in-flight recount must not un-dim while a newer one is pending.
        .finally(() => {
          if (latestCountKey.current === selectionKey) setCountLoading(false);
        });
    }, COUNT_DEBOUNCE_MS);
    return () => {
      if (countTimeout.current) clearTimeout(countTimeout.current);
    };
  }, [selectionKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Authoritative distinct figures once the server count for THIS selection is in.
  // Until then, fall back to the provisional sum — which is exact for a single
  // archetype/category (no overlap) and, crucially, never 0 while something is
  // selected, so the total never flashes to 0 during a recount (we only dim it).
  const freshCount = counted?.key === selectionKey ? counted.result : null;
  const twins = freshCount ? freshCount.twin_count : totals.provisionalTwins;
  const cost = twins * unitPrice;
  // Don't allow starting while a distinct recount is still in flight — the
  // provisional (pre-dedup) count would over-bill; wait for the authoritative one.
  const canStart = !!useCase && twins > 0 && !countLoading;
  const meta = useCase ? USE_CASE_META[useCase] : null;

  // Reuse the study created on a prior pass (Back from Review) ONLY when the user has
  // NOT changed it — same use case AND same audience. A reused study only has its inputs
  // patched server-side (never its use_case or selections), so reusing it after a change
  // would make Review — the pre-payment screen — show the ORIGINAL study's use case and
  // audience while the user believes (and is billed) for the new one. When anything
  // changed, start a fresh study. preset is read live so a screen-1 visit that clears
  // router state also forces a fresh study.
  //
  // Returns the abandoned id as `supersededStudyId` rather than dropping it. The old
  // assumption — "the stale draft is harmless (never submitted, not billed)" — held only
  // for `draft`, which history hides. A study the user already took to checkout is
  // `pending_payment`, which history SHOWS, so silently abandoning it left a ghost
  // "Awaiting payment" row for a study they had in fact edited and run. Worse, its Stripe
  // session stayed payable. Naming it here lets the server retire it and expire that
  // session.
  const resolveStudyIds = (): { studyId?: number; supersededStudyId?: number } => {
    // Baseline from ONE source so a preset studyId is never paired with the draft's
    // audience (or vice-versa): the in-app preset when it carries a studyId, else the
    // session draft (browser Back / reload).
    const draft = loadPaidDraft();
    const fromPreset = preset.studyId != null;
    const baseStudyId = fromPreset ? preset.studyId : draft?.studyId;
    const baseUseCase = fromPreset ? preset.useCase : draft?.useCase;
    // An id abandoned on an EARLIER pass that has not been actioned yet — the user
    // edited, came back and edited again before reaching Set-up. Without this it would
    // be dropped on the second pass and the ghost row would survive after all.
    const pending = draft?.supersededStudyId;
    if (!baseStudyId) return { supersededStudyId: pending };
    // A changed USE CASE also cannot reuse the row, and equally strands it.
    if (baseUseCase !== useCase) return { supersededStudyId: baseStudyId };
    const baseSelections = fromPreset ? (preset.selections ?? []) : (draft?.selections ?? []);
    const prev = baseSelections
      .map((s) => s.archetype_id)
      .sort()
      .join('|');
    const curr = totals.selections
      .map((s) => s.archetype_id)
      .sort()
      .join('|');
    return prev === curr
      ? { studyId: baseStudyId, supersededStudyId: pending }
      : { supersededStudyId: baseStudyId };
  };

  const onStart = () => {
    if (!canStart) return;
    const { studyId, supersededStudyId } = resolveStudyIds();
    // Keep the typed form when the use case is unchanged (only the audience changed) so
    // going Back to rebuild the audience doesn't discard the questions/goals entered.
    const draft = loadPaidDraft();
    const savedFormData = draft?.useCase === useCase ? draft?.savedFormData : undefined;
    // Persist so a reload on the fill/review step rebuilds the wizard (#73).
    saveDraft({
      useCase: useCase!,
      selections: totals.selections,
      twinCount: twins,
      paid: true,
      studyId,
      supersededStudyId,
      savedFormData,
    });
    navigate(MARKETPLACE_FILL_PATH, {
      state: {
        useCase,
        selections: totals.selections,
        twinCount: twins,
        paid: true,
        studyId,
        supersededStudyId,
        savedFormData,
      },
    });
  };

  if (error)
    return (
      <MarketplaceErrorScreen
        title="We couldn’t load this page"
        message="Something went wrong setting up your study. Please try again."
        onRetry={() => window.location.reload()}
      />
    );

  return (
    <MarketplaceShell>
      <StepIndicator step={screen} labels={WIZARD_STEP_LABELS} />

      {/* ── Screen 1: use case (flow entry — no back affordance) ────── */}
      {screen === 1 && (
        <>
          <div className="mb-7 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1 className="text-[22px] font-medium text-[#111]">
                What kind of study do you want to run?
              </h1>
              <p className="mt-1.5 text-[13px] text-[#6b6b6b]">
                Pick one use case. You can run a different one next time.
              </p>
            </div>
            <PriceTag cents={unitPrice} />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {loadingMeta
              ? Array.from({ length: 4 }).map((_, i) => <UseCaseCardSkeleton key={i} />)
              : useCases.map((uc) => (
                  <UseCaseCard
                    key={uc.key}
                    useCaseKey={uc.key}
                    selected={useCase === uc.key}
                    onClick={() => selectUseCase(uc.key)}
                    freeAvailable={false}
                  />
                ))}
          </div>

          {/* No Back: this is the first step and /marketplace now forwards straight
              back into the wizard, so the button navigated to where you already were. */}
          <WizardNav
            onContinue={() => setScreen(2)}
            continueDisabled={!useCase}
            hint={!useCase ? 'Choose a use case to continue' : undefined}
          />
        </>
      )}

      {/* ── Screen 2: audience builder ─────────────────────────────── */}
      {screen === 2 && meta && (
        <>
          <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1 className="text-[22px] font-medium text-[#111]">Build your audience</h1>
              <p className="mt-1.5 text-[13px] text-[#6b6b6b]">
                Select Breakfast Categories and Segments. Cost updates as you build.
              </p>
            </div>
            <PriceTag cents={unitPrice} />
          </div>

          <MktCard className="p-5">
            <div className="mb-2.5 flex items-baseline justify-between">
              <div className="text-[12px] text-[#6b6b6b]">Category</div>
              <div className="text-[12px] text-[#6b6b6b]">Select one or more</div>
            </div>
            <div className="flex flex-wrap gap-2">
              {subsLoading && subs.length === 0 && <PillSkeletons count={6} />}
              {subs.map((sc) => (
                <Pill
                  key={sc.slug}
                  active={activeCats.includes(sc.slug)}
                  showCheck
                  onClick={() => toggleCat(sc.slug)}
                >
                  {sc.name}
                </Pill>
              ))}
            </div>

            <div className="my-5 h-px bg-black/10" />

            {/* Per-category archetype blocks */}
            <div className="mb-6 flex flex-col gap-2.5">
              {activeCats.map((slug) => {
                const archs = archsOf(slug);
                const set = sel[slug] || new Set();
                const label = subs.find((s) => s.slug === slug)?.name || slug;
                // The whole-category total is the sub-category's own distinct Twin
                // count from the catalog (authoritative), not a bucket sum.
                const total =
                  subs.find((s) => s.slug === slug)?.twin_count ??
                  archs.reduce((n, a) => n + a.twin_count, 0);
                // Selected-in-category: prefer the server's per-category distinct
                // count; fall back to the bucket sum (exact within a category, whose
                // cohorts are disjoint) until the count resolves.
                const selTwins =
                  freshCount?.by_sub_category?.[slug] ??
                  archs.reduce((n, a) => n + (set.has(a.archetype_id) ? a.twin_count : 0), 0);
                const allSel = isAll(slug);
                // `cache[slug]` is undefined until THAT category's archetypes land
                // (archsOf() silently returns [] meanwhile), so an activated category
                // rendered an empty body with no sign it was still loading.
                const archsLoading = cache[slug] === undefined;
                return (
                  <div key={slug} className="overflow-hidden rounded-xl border border-black/10">
                    <div className="flex items-center justify-between gap-3 border-b border-black/10 bg-[#f5f5f3] px-4 py-3">
                      <div className="min-w-0 truncate text-[13px] font-medium text-[#111]">
                        {label}
                      </div>
                      <div className="flex-shrink-0 whitespace-nowrap text-[12px] text-[#6b6b6b]">
                        {archsLoading ? (
                          'Loading Segments…'
                        ) : (
                          <>
                            {set.size} of {archs.length} selected ·{' '}
                            <span className="font-medium text-[#111]">{selTwins} Twins</span>
                          </>
                        )}
                      </div>
                    </div>
                    {archsLoading && (
                      <div className="flex flex-wrap gap-2 p-4">
                        <PillSkeletons count={4} />
                      </div>
                    )}
                    {/* All row */}
                    <div
                      onClick={() => toggleAll(slug)}
                      className={`flex cursor-pointer items-center justify-between border-b border-black/10 px-4 py-2.5 ${allSel ? 'bg-[#E8FCFA]' : 'bg-[#f5f5f3] hover:bg-[#ececea]'}`}
                    >
                      <div className="flex items-center gap-2.5">
                        <Checkbox checked={allSel} />
                        <span className="text-[13px] font-medium text-[#111]">All Segments</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[12px] text-[#6b6b6b]">{total} Twins</span>
                        <span className="text-[12px] font-medium text-[#6b6b6b]">
                          {dollars(total * unitPrice)}
                        </span>
                      </div>
                    </div>
                    {/* Archetype rows */}
                    {archs.map((a) => {
                      const checked = set.has(a.archetype_id);
                      return (
                        <div
                          key={a.archetype_id}
                          onClick={() => toggleArch(slug, a.archetype_id)}
                          onMouseEnter={() => showSegment(label, a)}
                          onMouseLeave={hideSegment}
                          className={`flex cursor-pointer items-center justify-between border-b border-black/10 px-4 py-2.5 last:border-b-0 ${checked ? 'bg-[#E8FCFA]' : 'hover:bg-[#f5f5f3]'}`}
                        >
                          <div className="flex items-center gap-2.5">
                            <Checkbox checked={checked} />
                            <span className="text-[13px] text-[#111]">{a.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[12px] text-[#6b6b6b]">{a.twin_count} Twins</span>
                            <span className="text-[12px] font-medium text-[#6b6b6b]">
                              {dollars(a.twin_count * unitPrice)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                    {archs.length === 0 && (
                      <div className="px-4 py-3 text-[13px] text-[#999]">
                        {cache[slug] === undefined
                          ? 'Loading Segments…'
                          : 'No Segments available for this Category.'}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Checkout bar */}
            <div className="flex flex-col gap-4 rounded-xl border border-black/10 p-4 md:flex-row md:flex-wrap md:items-center md:justify-between">
              <div className="flex flex-wrap items-center gap-4 md:gap-6">
                <div>
                  <div className="text-[11px] text-[#6b6b6b]">AI Consumer Twins selected</div>
                  <div
                    className={`text-[22px] font-medium text-[#111] transition-opacity ${countLoading ? 'opacity-50' : ''}`}
                  >
                    {twins}
                  </div>
                  {new Set(totals.selections.map((s) => s.sub_category)).size > 1 && (
                    <div className="mt-0.5 max-w-[220px] text-[10px] leading-snug text-[#9CA3AF]">
                      Some people belong to more than one category — they’re counted (and billed)
                      once.
                    </div>
                  )}
                </div>
                <div className="h-8 w-px bg-black/10" />
                <div>
                  <div className="text-[11px] text-[#6b6b6b]">Total cost</div>
                  <div
                    className={`text-[22px] font-medium text-[#111] transition-opacity ${countLoading ? 'opacity-50' : ''}`}
                  >
                    {dollars(cost)}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 md:max-w-[220px]">
                  {totals.tags.slice(0, 3).map((t, i) => (
                    <Tag key={i}>
                      {t.label} · {t.n}
                    </Tag>
                  ))}
                  {totals.tags.length > 3 && <Tag>+{totals.tags.length - 3} more</Tag>}
                </div>
              </div>
            </div>
          </MktCard>

          <WizardNav
            onBack={() => setScreen(1)}
            onContinue={onStart}
            continueDisabled={!canStart}
            hint={
              twins === 0 ? (
                'Select at least one Segment to continue'
              ) : twins < MIN_RECOMMENDED_TWINS && !countLoading ? (
                <span className="text-amber-700">
                  Small samples may be less statistically reliable — we recommend at least{' '}
                  {MIN_RECOMMENDED_TWINS} Twins.
                </span>
              ) : undefined
            }
          />
        </>
      )}
      <SegmentHoverCard segment={hoverSeg} />
    </MarketplaceShell>
  );
};

export default BuildAudienceScreen;
